import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { paths } from 'src/common/config/TextMessage';
import { usePermissions } from './usePermissions';
import type { Permission } from './types';

interface RequirePermissionProps {
  permission: Permission;
  children: ReactNode;
  /**
   * Override the redirect target when access is denied.
   * When omitted, the redirect is chosen automatically based on account status and plan.
   */
  redirectTo?: string;
}

/**
 * Route-level permission guard. Wrap individual route elements with this
 * instead of adding if/else blocks to ProtectedRoutes.
 *
 * Redirect logic (when redirectTo is not provided):
 *  - paused / expired / no_plan account → /billing
 *  - localseo user without access       → /local-seo/business-profile
 *  - all other denials                  → /dashboard
 *
 * @example — in router.tsx
 * <Route
 *   path="lead-generation"
 *   element={
 *     <RequirePermission permission="leads.form_builder">
 *       <Suspense fallback={<SuspenseLoader />}>
 *         <LeadGeneration />
 *       </Suspense>
 *     </RequirePermission>
 *   }
 * />
 */
export const RequirePermission = ({
  permission,
  children,
  redirectTo,
}: RequirePermissionProps) => {
  const { can, accountStatus, plan } = usePermissions();

  if (can(permission)) return <>{children}</>;

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  // Smart redirect: degraded accounts always go to billing first.
  if (
    accountStatus === 'paused' ||
    accountStatus === 'expired' ||
    accountStatus === 'no_plan'
  ) {
    return <Navigate to={paths.billing} replace />;
  }

  // LocalSEO users land on their hub, not the main dashboard.
  if (plan === 'localseo') {
    return <Navigate to={paths.localChecker} replace />;
  }

  return <Navigate to={paths.dashboard} replace />;
};
